<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { chargeClosuresApi, type ChargeClosure, type ClosurePreview } from '@/api/charge-closures';
import { taxDocumentsApi, type TaxDocument } from '@/api/tax-documents';
import { taxPartiesApi, type TaxParty } from '@/api/tax-parties';
import { apiErrorCode, apiErrorStatus, apiErrorText } from '@/utils/api-errors';
import { formatCurrency } from '@/utils/currency';
import { centsToAmount, documentKindLabel } from '@/utils/tax-documents';
import { formatShortDate } from '@/utils/patient-status';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// "Emitir DTE" for one patient and the range on screen: a stateless preview
// (what gets frozen, who receives it, how the country breaks the total down),
// then the closure is created and its document queued. When the closure lands
// but the emission is refused (credentials, provider down) the dialog keeps the
// closure and offers to retry only the emission, so nothing is frozen twice.
interface Props {
  modelValue: boolean;
  patientId: number;
  patientName: string;
  fromDate: string;
  toDate: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'emitted': [closure: ChargeClosure, document: TaxDocument];
  // The closure exists even when its document is then refused: the period's
  // charges changed state, so whoever lists them should reload.
  'closure-created': [closure: ChargeClosure];
}>();

const { t } = useI18n();

const PERCENT = 100;
const NOT_FOUND = 404;

const preview = ref<ClosurePreview | null>(null);
const party = ref<TaxParty | null>(null);
const partyNotice = ref('');
const createdClosure = ref<ChargeClosure | null>(null);
// Only a refused send offers a retry. Keying this off `createdClosure` made the
// dialog announce a failure in the gap between the two writes of a successful
// emission, and named a "cierre" the user had never been told about.
const emissionFailed = ref(false);
const loadingPreview = ref(false);
const busy = ref(false);
const errorText = ref('');

const period = computed(() => ({
  patientId: props.patientId, fromDate: props.fromDate, toDate: props.toDate,
}));

function money(cents: number): string {
  return formatCurrency(centsToAmount(cents, preview.value?.currency ?? 'CLP'));
}

async function lookupParty(representativeId: number, documentKind: string) {
  party.value = null;
  partyNotice.value = '';
  try {
    party.value = await taxPartiesApi.show(representativeId, documentKind);
  } catch (error) {
    // The lookup is a preview courtesy; the emission validates the recipient again.
    // A 404 is an answer (nothing on record), not an outage.
    partyNotice.value = apiErrorStatus(error) === NOT_FOUND ?
      t('chargeClosures.emit.partyNotFound') :
      apiErrorText(error, t('chargeClosures.emit.partyUnavailable'));
  }
}

async function loadPreview() {
  loadingPreview.value = true;
  errorText.value = '';
  try {
    preview.value = await chargeClosuresApi.preview(period.value);
    const recipient = preview.value.recipient;
    if (recipient?.valid) {
      await lookupParty(recipient.representativeId, preview.value.taxes.documentKind);
    }
  } catch (error) {
    preview.value = null;
    errorText.value = apiErrorText(error, t('chargeClosures.emit.previewError'));
  } finally {
    loadingPreview.value = false;
  }
}

watch(() => props.modelValue, (open) => {
  if (!open) return;
  createdClosure.value = null;
  emissionFailed.value = false;
  party.value = null;
  loadPreview();
}, { immediate: true });

const recipient = computed(() => preview.value?.recipient ?? null);
// Every address the DTE email goes to once the authority accepts it: the first
// (the billing principal) as addressee, the rest in copy.
const mailEmails = computed(() => {
  const [principal, ...copied] = (preview.value?.mailRecipients ?? [])
    .map((mailRecipient) => mailRecipient.email);
  if (!principal) return '';
  if (copied.length === 0) return principal;

  return t('chargeClosures.emit.withCopy', { email: principal, copied: copied.join(', ') });
});
const taxes = computed(() => preview.value?.taxes ?? null);
const nothingToBill = computed(() => (
  !!preview.value && preview.value.shiftsCount === 0 && preview.value.movementsTotalCents <= 0
));
const recipientBlocker = computed(() => {
  if (!preview.value) return '';
  if (!recipient.value) return t('chargeClosures.emit.noRepresentative');
  if (!recipient.value.valid) {
    return t('chargeClosures.emit.invalidTaxId', { label: recipient.value.taxIdLabel });
  }
  if (party.value && !party.value.registered) return t('chargeClosures.emit.unregistered');

  return '';
});
const documentLabel = computed(() => (
  taxes.value ? documentKindLabel(taxes.value.documentKind, taxes.value.documentCode, t) : ''
));
// The company routes each kind of document to a provider account; without a
// ready one for this kind nothing can be issued, whatever the closure says.
const providerBlocker = computed(() => {
  if (!taxes.value || taxes.value.providerReady) return '';

  return t('chargeClosures.emit.providerNotReady', { kind: documentLabel.value });
});
const blocked = computed(() => (
  nothingToBill.value || !!recipientBlocker.value || !!providerBlocker.value
));
const taxRatePercent = computed(() => (
  taxes.value ? `${Math.round(parseFloat(taxes.value.rate) * PERCENT)}%` : ''
));
const roundingNotice = computed(() => {
  if (!taxes.value || !preview.value || taxes.value.roundingDeltaCents === 0) return '';

  return t('chargeClosures.emit.roundingDelta', {
    delta: money(Math.abs(taxes.value.roundingDeltaCents)),
    closureTotal: money(preview.value.totalAmountCents),
  });
});

// Nothing below a blocker is actionable: the amounts are hidden so the dialog
// says only what is wrong and what would fix it.
const showAmounts = computed(() => !!taxes.value && !blocked.value);

function fail(error: unknown, fallbackKey: string) {
  errorText.value = apiErrorText(error, t(fallbackKey));
}

// The closure is the frozen selection; a moved total means the range changed
// under us, so the preview is reloaded rather than created blind.
async function createClosure(): Promise<ChargeClosure | null> {
  if (createdClosure.value) return createdClosure.value;
  try {
    createdClosure.value = await chargeClosuresApi.create({
      ...period.value, expectedTotalAmountCents: preview.value?.totalAmountCents,
    });
    emit('closure-created', createdClosure.value);

    return createdClosure.value;
  } catch (error) {
    if (apiErrorCode(error) === 'totals_mismatch') {
      await loadPreview();
      errorText.value = t('chargeClosures.emit.totalsChanged');
    } else {
      fail(error, 'chargeClosures.emit.createError');
    }

    return null;
  }
}

async function emitDocument(closure: ChargeClosure) {
  try {
    const document = await taxDocumentsApi.create(closure.id);
    emit('emitted', closure, document);
    emit('update:modelValue', false);
  } catch (error) {
    emissionFailed.value = true;
    fail(error, 'chargeClosures.emit.documentError');
  }
}

async function confirm() {
  if (busy.value || blocked.value) return;
  busy.value = true;
  errorText.value = '';
  emissionFailed.value = false;
  try {
    const closure = await createClosure();
    if (closure) await emitDocument(closure);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    :max-width="620"
    :title="t('chargeClosures.emit.title')"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #default>
      <div
        v-if="loadingPreview"
        class="d-flex justify-center pa-4"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>
      <!-- Three type levels and nothing else: a quiet overline names each block,
           the one value that matters in it is set in body-1, everything
           supporting is body-2 or caption in low emphasis. -->
      <template v-else-if="preview">
        <div class="text-h6">
          {{ patientName }}
        </div>
        <div class="text-body-2 text-medium-emphasis">
          {{ formatShortDate(fromDate) }} – {{ formatShortDate(toDate) }}
        </div>

        <v-divider class="my-4" />

        <div class="text-overline text-medium-emphasis">
          {{ t('chargeClosures.emit.recipient') }}
        </div>
        <!-- The recipient stays on screen even when it is the problem: being told
             a tax id is wrong is no use without being shown which one. -->
        <div
          v-if="recipient"
          class="text-body-1"
        >
          {{ recipient.name }}
        </div>
        <div
          v-if="recipient"
          class="text-body-2 text-medium-emphasis"
        >
          {{ recipient.taxIdLabel }} {{ recipient.taxId }}
          <template v-if="party?.name">
            · {{ t('chargeClosures.emit.registeredAs', { name: party.name }) }}
          </template>
          <template v-else-if="partyNotice">
            · {{ partyNotice }}
          </template>
        </div>
        <v-alert
          v-if="recipientBlocker"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ recipientBlocker }}
        </v-alert>

        <template v-if="showAmounts && taxes">
          <v-divider class="my-4" />

          <div class="text-overline text-medium-emphasis">
            {{ t('chargeClosures.emit.document') }}
          </div>
          <div class="text-body-1 mb-2">
            {{ documentLabel }}
          </div>

          <div class="d-flex justify-space-between text-body-2 py-1">
            <span>{{ t('chargeClosures.emit.shifts', { count: preview.shiftsCount }) }}</span>
            <span>{{ money(preview.shiftsTotalCents) }}</span>
          </div>
          <div
            v-if="preview.movementsTotalCents !== 0"
            class="d-flex justify-space-between text-body-2 py-1"
          >
            <span>{{ t('chargeClosures.emit.adjustments') }}</span>
            <span>{{ money(preview.movementsTotalCents) }}</span>
          </div>
          <div
            v-if="taxes.taxableCents > 0"
            class="d-flex justify-space-between text-body-2 text-medium-emphasis py-1"
          >
            <span>{{ t('chargeClosures.emit.taxable') }}</span>
            <span>{{ money(taxes.taxableCents) }}</span>
          </div>
          <div
            v-if="taxes.exemptCents > 0"
            class="d-flex justify-space-between text-body-2 text-medium-emphasis py-1"
          >
            <span>{{ t('chargeClosures.emit.exempt') }}</span>
            <span>{{ money(taxes.exemptCents) }}</span>
          </div>
          <div
            v-if="taxes.taxCents > 0"
            class="d-flex justify-space-between text-body-2 text-medium-emphasis py-1"
          >
            <span>{{ t('chargeClosures.emit.tax', { rate: taxRatePercent }) }}</span>
            <span>{{ money(taxes.taxCents) }}</span>
          </div>

          <v-divider class="my-2" />

          <!-- The anchor of the dialog: the number the user is agreeing to. -->
          <div class="d-flex justify-space-between text-body-1 font-weight-bold">
            <span>{{ t('chargeClosures.emit.documentTotal') }}</span>
            <span>{{ money(taxes.totalCents) }}</span>
          </div>
          <p
            v-if="roundingNotice"
            class="text-caption text-medium-emphasis mt-1 mb-0"
          >
            {{ roundingNotice }}
            <v-icon
              icon="mdi-information-outline"
              size="14"
            />
            <v-tooltip
              activator="parent"
              location="top"
              max-width="360"
            >
              {{ t('chargeClosures.emit.roundingExplained') }}
            </v-tooltip>
          </p>
        </template>

        <v-alert
          v-if="nothingToBill"
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          {{ t('chargeClosures.emit.nothingToBill') }}
        </v-alert>
        <v-alert
          v-if="providerBlocker"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          {{ providerBlocker }}
        </v-alert>
        <p
          v-if="preview.invoicedShiftsCount > 0"
          class="text-caption text-medium-emphasis mt-3 mb-0"
        >
          {{ t('chargeClosures.emit.alreadyInvoiced', {
            count: preview.invoicedShiftsCount, total: money(preview.invoicedTotalCents),
          }) }}
        </p>
      </template>
      <v-alert
        v-if="errorText"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-4"
      >
        {{ errorText }}
      </v-alert>
      <template v-if="showAmounts">
        <p class="text-caption text-medium-emphasis mt-4 mb-0">
          {{ t('chargeClosures.emit.asynchronous') }}
        </p>
        <!-- Naming the address here is the last cheap moment to catch a wrong or
             missing one: after the SII accepts, the mail has already gone. -->
        <p
          v-if="mailEmails"
          class="text-caption text-medium-emphasis mt-1 mb-0"
        >
          {{ t('chargeClosures.emit.willEmail', { email: mailEmails }) }}
        </p>
        <p
          v-else
          class="text-caption text-warning mt-1 mb-0"
        >
          {{ t('chargeClosures.emit.willNotEmail') }}
        </p>
      </template>
    </template>
    <template #actions="{ close }">
      <v-btn
        variant="text"
        @click="close"
      >
        {{ t('chargeClosures.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="busy"
        :disabled="loadingPreview || !preview || blocked"
        @click="confirm"
      >
        {{ emissionFailed ? t('chargeClosures.emit.retry') : t('chargeClosures.emit.confirm') }}
      </v-btn>
    </template>
  </responsive-dialog>
</template>
